import { useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { Check, Layers, Receipt, Wallet } from "lucide-react";

const APP_URL = "https://schema-analysis-hub.emergent.host";
const PRICING_URL = `${APP_URL}/pricing`;

const selfServe = [
  {
    name: "Starter",
    price: "A$0",
    cadence: "forever",
    priceAnnual: "A$0",
    cadenceAnnual: "forever",
    schemas: "1 local LGA schema",
    cta: "Start free",
    href: APP_URL,
    features: [
      "Upload, parse & read ADAC XML",
      "Asset inspection",
      "Schema validation",
      "Basic validation PDF",
    ],
  },
  {
    name: "Single Authority",
    price: "A$29",
    cadence: "/ user / month",
    priceAnnual: "A$290",
    cadenceAnnual: "/ user / year",
    schemas: "1 authority schema (your LGA)",
    cta: "Choose plan",
    href: PRICING_URL,
    features: [
      "Everything in Starter",
      "Per-asset-type tolerances + profiles",
      "ADAC Builder (map digitiser)",
      "Compare design vs as-built",
      "Full council Submission Pack PDF",
    ],
  },
  {
    name: "All Authorities",
    price: "A$49",
    cadence: "/ user / month",
    priceAnnual: "A$490",
    cadenceAnnual: "/ user / year",
    schemas: "All authority QA schemas",
    cta: "Upgrade",
    href: PRICING_URL,
    highlight: true,
    features: [
      "Everything in Single Authority",
      "All authority QA schemas",
      "Map bulk linking",
      "Long & cross-section profiles",
      "TIN surfaces + cut/fill volumes",
      "Projects workspace",
    ],
  },
];

const orgs = [
  {
    name: "Enterprise",
    badge: "For large firms",
    price: "Custom",
    cadence: "volume pricing",
    schemas: "All schemas · many seats",
    cta: "Contact sales",
    href: "/contact",
    dark: true,
    features: [
      "Volume per-seat discounts",
      "Named seats — no shared logins",
      "SSO & central user management",
      "Priority support & onboarding",
      "Consolidated annual billing",
    ],
  },
  {
    name: "Council",
    badge: "For Councils",
    price: "Custom",
    cadence: "annual license",
    schemas: "Unlimited schemas & seats",
    cta: "Enquire about council pricing",
    href: "/contact",
    council: true,
    features: [
      "Unlimited reviewer seats",
      "Branded submission inbox",
      "Validate incoming submissions",
      "Mandate one QA standard",
    ],
  },
];

function PlanCard({ p, delay, annual }) {
  const slug = p.name.toLowerCase().replace(/\s+/g, "-");
  const isDark = p.highlight || p.dark;
  const price = annual && p.priceAnnual ? p.priceAnnual : p.price;
  const cadence = annual && p.cadenceAnnual ? p.cadenceAnnual : p.cadence;
  const ctaCls = `mt-6 block w-full text-center px-5 py-3 text-sm font-medium rounded-sm transition-colors ${
    isDark || p.council
      ? "bg-[#0033A0] text-white hover:bg-[#00257A]"
      : "bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50"
  }`;
  return (
    <Reveal
      delay={delay}
      hoverLift
      className={`flex flex-col rounded-2xl p-7 md:p-8 border transition-[box-shadow,border-color] duration-300 hover:shadow-lg ${
        isDark
          ? "bg-[#121316] border-zinc-800 text-white shadow-xl"
          : p.council
          ? "bg-[#E6EBF5]/50 border-[#0033A0]/30 hover:border-[#0033A0]/60"
          : "bg-white border-zinc-200 hover:border-zinc-400"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className={`font-heading text-xl font-medium ${isDark ? "text-white" : "text-zinc-900"}`}>{p.name}</h3>
        {p.highlight && (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0033A0] text-white">Popular</span>
        )}
        {p.badge && (
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isDark ? "bg-white/10 text-blue-200" : "bg-[#0033A0] text-white"
            }`}
          >
            {p.badge}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-baseline gap-1.5">
        <span className={`font-heading text-3xl font-semibold ${isDark ? "text-white" : "text-zinc-900"}`}>{price}</span>
        <span className={`text-sm ${isDark ? "text-zinc-400" : "text-zinc-500"}`}>{cadence}</span>
      </div>

      <div
        className={`mt-4 inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full text-xs font-medium ${
          isDark ? "bg-white/10 text-blue-200" : "bg-zinc-100 text-zinc-700"
        }`}
      >
        <Layers size={13} /> {p.schemas}
      </div>

      {p.href.startsWith("/") ? (
        <Link to={p.href} data-testid={`pricing-${slug}-cta`} className={ctaCls}>
          {p.cta}
        </Link>
      ) : (
        <a href={p.href} data-testid={`pricing-${slug}-cta`} className={ctaCls}>
          {p.cta}
        </a>
      )}

      <ul className="mt-7 space-y-3">
        {p.features.map((f) => (
          <li key={f} className={`flex items-start gap-3 text-sm ${isDark ? "text-zinc-200" : "text-zinc-700"}`}>
            <Check size={16} className={`shrink-0 mt-0.5 ${isDark ? "text-green-400" : "text-[#0033A0]"}`} />
            {f}
          </li>
        ))}
      </ul>
    </Reveal>
  );
}

export default function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section data-testid="pricing-section" id="pricing" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-12">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0033A0] font-mono">Pricing</p>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-medium tracking-tight text-zinc-900 leading-tight">
            Pricing that scales with how many authority schemas you need.
          </h2>
          <p className="mt-4 text-zinc-600 text-lg">
            Small consultancies can licence just their local LGA schema; larger firms unlock every authority.
            <span className="text-zinc-900 font-medium"> Month-to-month, no lock-in contracts — cancel anytime.</span>
          </p>
        </Reveal>

        <Reveal className="mb-10 grid sm:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-white p-5">
            <span className="shrink-0 h-9 w-9 grid place-items-center bg-[#E6EBF5] text-[#0033A0] rounded-sm">
              <Receipt size={18} strokeWidth={1.7} />
            </span>
            <p className="text-sm text-zinc-700 leading-relaxed">
              <span className="font-medium text-zinc-900">Less than one rejected resubmission.</span> A single reworked
              ADAC handover — rework hours plus delay — costs more than a full year of WebADAC.
            </p>
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-white p-5">
            <span className="shrink-0 h-9 w-9 grid place-items-center bg-[#E6EBF5] text-[#0033A0] rounded-sm">
              <Wallet size={18} strokeWidth={1.7} />
            </span>
            <p className="text-sm text-zinc-700 leading-relaxed">
              <span className="font-medium text-zinc-900">~1/10th the cost of a full ADAC design seat.</span> Full
              ADAC-capable design suites run ~A$4,000/user/yr — WebADAC is the focused QA & submission layer.
            </p>
          </div>
        </Reveal>

        {/* Billing toggle */}
        <Reveal className="mb-8 flex items-center gap-4">
          <div className="inline-flex items-center rounded-full border border-zinc-300 bg-white p-1" role="group" aria-label="Billing period">
            <button
              data-testid="billing-monthly-toggle"
              onClick={() => setAnnual(false)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                !annual ? "bg-[#0033A0] text-white" : "text-zinc-600 hover:text-zinc-900"
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-annual-toggle"
              onClick={() => setAnnual(true)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                annual ? "bg-[#0033A0] text-white" : "text-zinc-600 hover:text-zinc-900"
              }`}
            >
              Annual
            </button>
          </div>
          <span
            data-testid="annual-savings-badge"
            className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
              annual ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"
            }`}
          >
            Save ~2 months with annual billing
          </span>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {selfServe.map((p, i) => (
            <PlanCard key={p.name} p={p} delay={i * 0.07} annual={annual} />
          ))}
        </div>

        <p className="mt-5 text-xs text-zinc-500 font-mono">
          Per named user · seats aren't shared · prices in AUD, ex-GST · indicative, confirmed at sign-up · annual billing available (~2 months free).
        </p>

        <Reveal className="mt-16 mb-8">
          <h3 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">For teams &amp; organisations</h3>
          <p className="mt-2 text-zinc-600">
            Buying for a large firm or a Council? Talk to us about volume seats, SSO, and unlimited access.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
          {orgs.map((p, i) => (
            <PlanCard key={p.name} p={p} delay={i * 0.08} annual={annual} />
          ))}
        </div>

        <p className="mt-8 text-xs text-zinc-500 font-mono">
          Councils: pricing on enquiry. See plan details on the{" "}
          <a href={PRICING_URL} className="text-[#0033A0] hover:underline">ADAC Suite pricing page</a>.
        </p>
      </div>
    </section>
  );
}
